/**
 * ChatText: shows a chat message with light formatting so replies are easy to
 * read. Bot replies use markdown-style text ("- item", "1. step", "**bold**",
 * "## Heading"); without this the visitor sees the raw stars and dashes.
 * Builds React elements only (never innerHTML), so message text cannot inject markup.
 */
import React from 'react';

const BULLET = /^\s*(?:[-*•])\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;

function inline(text, keyBase) {
  // **bold** (or __bold__); stray single stars are dropped
  return text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g).filter(Boolean).map((part, i) =>
    /^(\*\*|__).+(\*\*|__)$/.test(part)
      ? <strong key={`${keyBase}-${i}`} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>
      : part.replace(/(^|\s)\*(\S[^*]*\S|\S)\*(?=\s|$|[.,!?])/g, '$1$2')
  );
}



export function toBlocks(text) {
  const blocks = [];
  const push = (kind, line) => {
    const last = blocks[blocks.length - 1];
    if (last && last.kind === kind && kind !== 'h') last.lines.push(line);
    else blocks.push({ kind, lines: [line] });
  };
  String(text || '').split(/\r?\n/).forEach((raw) => {
    if (!raw.trim()) { blocks.push({ kind: 'p', lines: [] }); return; }
    let m;
    if ((m = raw.match(HEADING))) push('h', m[1]);
    else if ((m = raw.match(BULLET))) push('ul', m[1]);
    else if ((m = raw.match(NUMBERED))) push('ol', m[2]);
    else push('p', raw.trim());
  });
  return blocks.filter((b) => b.lines.length);
}

export default function ChatText({ text }) {
  const blocks = toBlocks(text);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {blocks.map((b, bi) => {
        const k = `b${bi}`;
        if (b.kind === 'h') return <div key={k} style={{ fontWeight: 700 }}>{inline(b.lines[0], k)}</div>;
        if (b.kind === 'ul' || b.kind === 'ol') {
          const List = b.kind === 'ul' ? 'ul' : 'ol';
          return (
            <List key={k} style={{ margin: 0, paddingLeft: '18px', listStyle: b.kind === 'ul' ? 'disc' : 'decimal', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {b.lines.map((l, li) => <li key={`${k}-${li}`}>{inline(l, `${k}-${li}`)}</li>)}
            </List>
          );
        }
        return (
          <p key={k} style={{ margin: 0 }}>
            {b.lines.map((l, li) => (
              <React.Fragment key={`${k}-${li}`}>{li > 0 && <br />}{inline(l, `${k}-${li}`)}</React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
